import {
  trackEvent,
  trackPageView,
  trackTabSwitch,
  trackCalculation,
  trackAdvancedOption,
  trackExportAction,
  trackSocialShare,
  trackEducationInteraction,
  trackScenarioAction,
  trackPerformance,
} from './googleAnalytics';

import {
  addCalculationBreadcrumb,
  addExportBreadcrumb,
  addNavigationBreadcrumb,
  addBreadcrumb,
  reportPerformanceIssue,
  setUserContext,
} from './errorTracking';

// Combined tracking interface that sends data to both GA and Sentry
class UserTrackingService {
  private sessionId: string;
  private startTime: number;

  constructor() {
    this.sessionId = this.generateSessionId();
    this.startTime = Date.now();
    this.initializeSession();
  }

  private generateSessionId(): string {
    return `${Date.now()}-${Math.random().toString(36).substring(2, 9)}`;
  }

  private initializeSession() {
    // Set anonymous user context for Sentry
    setUserContext({
      id: this.sessionId,
      segment: 'calculator_user',
      properties: {
        sessionStart: new Date().toISOString(),
        userAgent: navigator.userAgent,
        language: navigator.language,
        timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
      },
    });

    // NOTE: the initial page_view is tracked once by App.tsx on mount; do not
    // fire it again here (would double-count pageviews). The canonical GA4
    // service (src/services/analyticsService.ts) also dedupes by location.
    addBreadcrumb('Session started', 'session', 'info', {
      sessionId: this.sessionId,
    });
  }

  // Page and navigation tracking
  trackPageView(pageTitle: string, pageLocation?: string) {
    trackPageView(pageTitle, pageLocation);
    addNavigationBreadcrumb('direct', pageTitle);
  }

  trackTabSwitch(fromTab: 'calculator' | 'education', toTab: 'calculator' | 'education') {
    trackTabSwitch(fromTab, toTab);
    addNavigationBreadcrumb(fromTab, toTab);
  }

  // Calculator interaction tracking
  trackCalculationStart(input: {
    incomeFrequency: 'monthly' | 'yearly';
    hasAdvancedOptions: boolean;
    ageCategory: string;
  }) {
    addCalculationBreadcrumb('calculation_started', {
      incomeFrequency: input.incomeFrequency,
      hasAdvancedOptions: input.hasAdvancedOptions,
      ageCategory: input.ageCategory,
    });
  }

  trackCalculationComplete(input: {
    incomeFrequency: 'monthly' | 'yearly';
    hasAdvancedOptions: boolean;
    calculationTime: number;
  }) {
    const calculationType = input.hasAdvancedOptions ? 'advanced' : 'basic';
    
    // Track in GA
    trackCalculation(calculationType, input.incomeFrequency);
    
    // Add breadcrumb to Sentry
    addCalculationBreadcrumb('calculation_completed', {
      calculationType,
      incomeFrequency: input.incomeFrequency,
      calculationTime: input.calculationTime,
    });

    // Track performance if calculation took too long
    if (input.calculationTime > 1000) {
      reportPerformanceIssue('calculation_time', input.calculationTime, 1000);
    }

    trackPerformance('calculation_time', input.calculationTime, 'milliseconds');
  }

  trackCalculationError(error: Error, context: Record<string, unknown>) {
    addCalculationBreadcrumb('calculation_failed', {
      errorMessage: error.message,
      context,
    });
  }

  // Advanced options tracking
  trackAdvancedOptionToggle(optionType: 'medical_aid' | 'pension' | 'travel_allowance', enabled: boolean) {
    trackAdvancedOption(optionType, enabled);
    addCalculationBreadcrumb(`advanced_option_${enabled ? 'enabled' : 'disabled'}`, {
      optionType,
      enabled,
    });
  }

  // Export and sharing tracking
  trackExportAttempt(exportType: 'pdf' | 'csv' | 'text' | 'print', source: string) {
    trackExportAction(exportType, source);
    addExportBreadcrumb(exportType, false); // Will be updated on success/failure
  }

  trackExportSuccess(exportType: 'pdf' | 'csv' | 'text' | 'print', source: string, fileSize?: number) {
    addExportBreadcrumb(exportType, true);
    addBreadcrumb('Export completed successfully', 'export', 'info', {
      exportType,
      source,
      fileSize,
    });
  }

  trackExportError(exportType: string, error: Error) {
    addExportBreadcrumb(exportType, false);
    addBreadcrumb('Export failed', 'export', 'error', {
      exportType,
      errorMessage: error.message,
    });
  }

  trackSocialShare(platform: string, contentType: string = 'calculation_result') {
    trackSocialShare(platform, contentType);
    addBreadcrumb('Content shared', 'social', 'info', {
      platform,
      contentType,
    });
  }

  // Education and FAQ tracking
  trackFAQInteraction(faqId: string, action: 'open' | 'close') {
    trackEducationInteraction(`faq_${action}`, faqId);
    addBreadcrumb(`FAQ ${action}`, 'education', 'info', {
      faqId,
      action,
    });
  }

  trackTaxYearChange(fromYear: string, toYear: string) {
    trackEducationInteraction('tax_year_change', `${fromYear}_to_${toYear}`);
    addBreadcrumb('Tax year changed', 'education', 'info', {
      fromYear,
      toYear,
    });
  }

  trackEducationContentView(contentId: string, timeSpent?: number) {
    addBreadcrumb('Education content viewed', 'education', 'info', {
      contentId,
      timeSpent,
    });
  }

  // Scenario comparison tracking
  trackScenarioSaved(scenarioData: { hasAdvancedOptions: boolean; incomeFrequency: string }) {
    trackScenarioAction('save');
    addBreadcrumb('Scenario saved', 'scenarios', 'info', {
      hasAdvancedOptions: scenarioData.hasAdvancedOptions,
      incomeFrequency: scenarioData.incomeFrequency,
    });
  }

  trackScenarioEdited(scenarioId: string) {
    trackScenarioAction('edit');
    addBreadcrumb('Scenario edited', 'scenarios', 'info', {
      scenarioId,
    });
  }

  trackScenarioDeleted(scenarioId: string) {
    trackScenarioAction('delete');
    addBreadcrumb('Scenario deleted', 'scenarios', 'info', {
      scenarioId,
    });
  }

  trackScenarioComparison(scenarioCount: number) {
    trackScenarioAction('compare', scenarioCount);
    addBreadcrumb('Scenarios compared', 'scenarios', 'info', {
      scenarioCount,
    });
  }

  // Performance tracking
  trackPerformanceMetric(metric: {
    name: string;
    value: number;
    unit: string;
    threshold?: number;
  }) {
    trackPerformance(metric.name, metric.value, metric.unit);
    
    if (metric.threshold && metric.value > metric.threshold) {
      reportPerformanceIssue(metric.name, metric.value, metric.threshold);
    }
  }

  trackCoreWebVitals(metrics: {
    fcp?: number;
    lcp?: number;
    cls?: number;
    inp?: number;
    ttfb?: number;
  }) {
    Object.entries(metrics).forEach(([metric, value]) => {
      if (value !== undefined) {
        this.trackPerformanceMetric({
          name: metric,
          value,
          unit: metric === 'cls' ? 'score' : 'milliseconds',
          threshold: this.getThreshold(metric),
        });
      }
    });
  }

  private getThreshold(metric: string): number {
    const thresholds: Record<string, number> = {
      fcp: 1800,   // First Contentful Paint
      lcp: 2500,   // Largest Contentful Paint
      cls: 0.1,    // Cumulative Layout Shift
      inp: 200,    // Interaction to Next Paint
      ttfb: 600,   // Time to First Byte
    };
    return thresholds[metric] || 1000;
  }

  // User engagement tracking
  trackTimeSpent(section: string, timeInSeconds: number) {
    addBreadcrumb(`Time spent in ${section}`, 'engagement', 'info', {
      section,
      timeInSeconds,
    });

    // Track significant time engagement
    if (timeInSeconds > 60) {
      trackEvent({
        event_name: 'engagement_time',
        event_category: 'engagement',
        event_label: section,
        value: Math.round(timeInSeconds),
        custom_parameters: {
          section,
          engagement_level: timeInSeconds > 300 ? 'high' : 'medium',
        },
      });
    }
  }

  // Session summary on page unload
  trackSessionEnd() {
    const sessionDuration = Math.round((Date.now() - this.startTime) / 1000);
    
    trackEvent({
      event_name: 'session_end',
      event_category: 'engagement',
      value: sessionDuration,
      custom_parameters: {
        session_duration: sessionDuration,
        session_id: this.sessionId,
      },
    });

    addBreadcrumb('Session ended', 'session', 'info', {
      sessionId: this.sessionId,
      duration: sessionDuration,
    });
  }

  // Error tracking
  trackError(error: Error, context?: Record<string, unknown>) {
    addBreadcrumb('Error occurred', 'error', 'error', {
      errorMessage: error.message,
      errorStack: error.stack,
      ...context,
    });
  }

  // Feature usage tracking
  trackFeatureUsage(feature: string, metadata?: Record<string, unknown>) {
    trackEvent({
      event_name: 'feature_used',
      event_category: 'features',
      event_label: feature,
      custom_parameters: {
        feature,
        ...metadata,
      },
    });

    addBreadcrumb(`Feature used: ${feature}`, 'features', 'info', {
      feature,
      ...metadata,
    });
  }
}

// Create singleton instance
export const userTracking = new UserTrackingService();

// Export convenience functions for easy importing (avoiding name conflicts)
export const trackCalculationStart = userTracking.trackCalculationStart.bind(userTracking);
export const trackCalculationComplete = userTracking.trackCalculationComplete.bind(userTracking);
export const trackCalculationError = userTracking.trackCalculationError.bind(userTracking);
export const trackAdvancedOptionToggle = userTracking.trackAdvancedOptionToggle.bind(userTracking);
export const trackExportAttempt = userTracking.trackExportAttempt.bind(userTracking);
export const trackExportSuccess = userTracking.trackExportSuccess.bind(userTracking);
export const trackExportError = userTracking.trackExportError.bind(userTracking);
export const trackFAQInteraction = userTracking.trackFAQInteraction.bind(userTracking);
export const trackTaxYearChange = userTracking.trackTaxYearChange.bind(userTracking);
export const trackEducationContentView = userTracking.trackEducationContentView.bind(userTracking);
export const trackScenarioSaved = userTracking.trackScenarioSaved.bind(userTracking);
export const trackScenarioEdited = userTracking.trackScenarioEdited.bind(userTracking);
export const trackScenarioDeleted = userTracking.trackScenarioDeleted.bind(userTracking);
export const trackScenarioComparison = userTracking.trackScenarioComparison.bind(userTracking);
export const trackPerformanceMetric = userTracking.trackPerformanceMetric.bind(userTracking);
export const trackCoreWebVitals = userTracking.trackCoreWebVitals.bind(userTracking);
export const trackTimeSpent = userTracking.trackTimeSpent.bind(userTracking);
export const trackSessionEnd = userTracking.trackSessionEnd.bind(userTracking);
export const trackError = userTracking.trackError.bind(userTracking);
export const trackFeatureUsage = userTracking.trackFeatureUsage.bind(userTracking);

// Re-export from googleAnalytics for convenience (use original functions to avoid conflicts)
export { trackPageView, trackTabSwitch, trackSocialShare } from './googleAnalytics';

// Set up session end tracking
if (typeof window !== 'undefined') {
  window.addEventListener('beforeunload', () => {
    userTracking.trackSessionEnd();
  });
}